import { Alert, Collapse, Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import { CoverageCalculationBreakdown } from 'features/libra/stepComponents/zoneAnalysis/components/CoverageCalculationBreakdown'
import { SmartImageViewer } from 'features/libra/stepComponents/zoneAnalysis/components/SmartImageViewer'
import { ZoneRow } from 'features/libra/stepComponents/zoneAnalysis/components/ZoneRow'
import {
  OverviewRow,
  useComputedZoneData,
  ZoneComputationParams,
} from 'features/libra/stepComponents/zoneAnalysis/useComputedZoneData'
import {
  calculateRequiredCoverage,
  type ConsequenceOfFailure,
  type CoverageCalculation,
  type DamageMechanism,
  getCofModifier,
  getEffectiveStrategyType,
  getEffectiveZoneArea,
  getEffectiveZoneHeight,
  getEffectiveZoneLength,
  InspectionStrategy,
  type NiiStrategy,
  type RbaInformation,
  ZONE_FIELD_IDS,
  zoneAnalysisConfigSchema,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  useZoneAnalysisData,
  ZoneAnalysisDataProvider,
} from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import {
  COVERAGE_COLUMNS,
  DAMAGE_MECHANISM_COLUMNS,
  RBA_KEYS,
  STRATEGY_KEYS,
  ZONE_DETAIL_FIELDS,
  ZONE_SECTION_KEYS,
  ZONE_SYNTHETIC_PREFIX,
} from 'features/libra/stepComponents/zoneAnalysis/zoneDetailConstants'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { RolodexEntryType, RolodexFilePath } from 'types'

const COF_COLORS: Record<string, string> = {
  low: 'green',
  medium: 'gold',
  high: 'red',
}

// ── Zone Overview Results ──────────────────────────────────────────
const overviewColumns: ColumnsType<OverviewRow> = [
  { title: 'Zone', dataIndex: 'zone', width: 60, align: 'center' },
  { title: 'Zone Description', dataIndex: 'description' },
  { title: 'Damage Mechanisms', dataIndex: 'damageMechanisms' },
  {
    title: 'Coverage %',
    dataIndex: 'coveragePercent',
    width: 110,
    align: 'center',
  },
  { title: 'Coverage', dataIndex: 'coverageArea', width: 110, align: 'center' },
]

const ZoneOverviewTable: FC<
  ZoneComputationParams & {
    stepId: string
  }
> = ({ zones, cofModifier, confidence, stepId }) => {
  const data = useComputedZoneData({ zones, cofModifier, confidence })

  const columns = useMemo<ColumnsType<OverviewRow>>(
    () =>
      overviewColumns.map((col) => {
        if (!('dataIndex' in col) || col.dataIndex === 'zone') return col
        return {
          ...col,
          render: (value: string, record: OverviewRow) => (
            <CommentIndicator
              anchor={{
                type: 'zone-property',
                stepId,
                zoneId: record.key,
                property: col.dataIndex as string,
              }}>
              {value}
            </CommentIndicator>
          ),
        }
      }),
    [stepId]
  )

  return (
    <Table
      columns={columns}
      dataSource={data}
      pagination={false}
      size='small'
      bordered
    />
  )
}

// ── Damage Mechanisms ──────────────────────────────────────────────

interface DmRow {
  key: string
  name: string
  rate: string
  justification: string
}

const dmColumns: ColumnsType<DmRow> = [
  { title: 'Damage Mechanism', dataIndex: DAMAGE_MECHANISM_COLUMNS.NAME },
  {
    title: 'Theoretical Rate/Susceptibility',
    dataIndex: DAMAGE_MECHANISM_COLUMNS.RATE,
    width: 220,
    align: 'center',
  },
  { title: 'Justification', dataIndex: DAMAGE_MECHANISM_COLUMNS.JUSTIFICATION },
]

const DamageMechanismsTable: FC<{
  damageMechanisms: DamageMechanism[]
  zoneIndex: number
  stepId: string
  zoneId: string
}> = ({ damageMechanisms, zoneIndex, stepId, zoneId }) => {
  const data = useMemo<DmRow[]>(
    () =>
      damageMechanisms.map((dm) => ({
        key: dm.id,
        name: dm.name,
        rate:
          dm.rate_value !== undefined
            ? `${dm.rate_value}${dm.rate_unit ?? ''}`
            : (dm.susceptibility ?? '—'),
        justification: dm.justification ?? '—',
      })),
    [damageMechanisms]
  )

  const columns = useMemo<ColumnsType<DmRow>>(
    () =>
      dmColumns.map((col) => {
        if (!('dataIndex' in col)) return col
        return {
          ...col,
          render: (value: string, record: DmRow) => (
            <CommentIndicator
              anchor={{
                type: 'zone-property',
                stepId,
                zoneId,
                property: `${ZONE_SYNTHETIC_PREFIX.DAMAGE_MECHANISM}.${record.key}.${col.dataIndex as string}`,
              }}>
              {value}
            </CommentIndicator>
          ),
        }
      }),
    [stepId, zoneId]
  )

  if (data.length === 0) return null

  return (
    <CommentIndicator
      anchor={{
        type: 'zone-property',
        stepId,
        zoneId,
        property: ZONE_SECTION_KEYS.DAMAGE_MECHANISMS,
      }}>
      <SectionBlock>
        <SectionTitle>Damage Mechanisms</SectionTitle>
        <Table
          title={() => (
            <TableHeader>
              Corrosion Risks and RBI Info (Zone {zoneIndex + 1})
            </TableHeader>
          )}
          columns={columns}
          dataSource={data}
          pagination={false}
          size='small'
          bordered
        />
      </SectionBlock>
    </CommentIndicator>
  )
}

// ── Zone Details ───────────────────────────────────────────────────

interface DetailRow {
  key: string
  parameter: string
  value: string
}

const detailColumns: ColumnsType<DetailRow> = [
  { title: 'Parameter', dataIndex: 'parameter', width: '45%' },
  { title: 'Value', dataIndex: 'value', align: 'center' },
]

const ZoneDetailsTable: FC<{
  zone: ZoneParams
  zoneIndex: number
  stepId: string
  zoneId: string
}> = ({ zone, zoneIndex, stepId, zoneId }) => {
  const data = useMemo<DetailRow[]>(() => {
    const rows: DetailRow[] = []
    const computedAccessors: Record<string, () => string | number | undefined> =
      {
        effectiveLength: () => getEffectiveZoneLength(zone),
        effectiveHeight: () => getEffectiveZoneHeight(zone),
        effectiveArea: () => {
          const a = getEffectiveZoneArea(zone)
          return a > 0 ? a.toFixed(2) : undefined
        },
      }

    for (const entry of ZONE_DETAIL_FIELDS) {
      const value =
        'resolve' in entry
          ? computedAccessors[entry.resolve]?.()
          : (zone as unknown as Record<string, string | number | undefined>)[
              entry.field
            ]
      if (value !== undefined && value !== null && value !== '') {
        rows.push({
          key: entry.label,
          parameter: entry.label,
          value: String(value),
        })
      }
    }

    return rows
  }, [zone])

  const columns = useMemo<ColumnsType<DetailRow>>(
    () =>
      detailColumns.map((col) => {
        if (!('dataIndex' in col) || col.dataIndex === 'parameter') return col
        return {
          ...col,
          render: (value: string, record: DetailRow) => (
            <CommentIndicator
              anchor={{
                type: 'zone-property',
                stepId,
                zoneId,
                property: `${ZONE_SYNTHETIC_PREFIX.DETAILS}.${record.key}`,
              }}>
              {value}
            </CommentIndicator>
          ),
        }
      }),
    [stepId, zoneId]
  )

  if (data.length === 0) return null

  return (
    <CommentIndicator
      anchor={{
        type: 'zone-property',
        stepId,
        zoneId,
        property: ZONE_SECTION_KEYS.ZONE_DETAILS,
      }}>
      <SectionBlock>
        <SectionTitle>Design and Function</SectionTitle>
        <Table
          title={() => <TableHeader>Zone {zoneIndex + 1} Details</TableHeader>}
          columns={columns}
          dataSource={data}
          pagination={false}
          size='small'
          bordered
        />
      </SectionBlock>
    </CommentIndicator>
  )
}

// ── NII Strategy Selection ─────────────────────────────────────────

interface StrategyRow {
  key: string
  question: string
  justification: string
}

const strategyColumns: ColumnsType<StrategyRow> = [
  { title: 'Question', dataIndex: 'question', width: '45%' },
  { title: 'Justification', dataIndex: 'justification' },
]

const STRATEGY_LABELS: Record<string, string> = {
  A: 'Type A — Confirmation Strategy',
  B: 'Type B — Quantitative (Predictable)',
  C: 'Type C — Quantitative (Unpredictable)',
}

const NiiStrategyTable: FC<{
  strategy: NiiStrategy | undefined
  zoneIndex: number
  stepId: string
  zoneId: string
}> = ({ strategy, zoneIndex, stepId, zoneId }) => {
  const effectiveType = getEffectiveStrategyType(strategy)

  const data = useMemo<StrategyRow[]>(() => {
    if (!strategy) return []
    const rows: StrategyRow[] = []

    if (strategy.degradation_likely !== undefined) {
      rows.push({
        key: STRATEGY_KEYS.DEGRADATION_LIKELY,
        question: `Degradation likely/measurable within two inspection intervals?\n${strategy.degradation_likely ? 'Yes' : 'No'}`,
        justification:
          strategy.degradation_likely_justification ??
          strategy.justification ??
          '—',
      })
    }

    if (strategy.degradation_predictable !== undefined) {
      rows.push({
        key: STRATEGY_KEYS.DEGRADATION_PREDICTABLE,
        question: `Degradation Predictable?\n${strategy.degradation_predictable ? 'Yes' : 'No'}`,
        justification:
          strategy.degradation_predictable_justification ??
          strategy.justification ??
          '—',
      })
    }

    if (strategy.degradation_severity !== undefined) {
      const isLowMedium =
        strategy.degradation_severity === 'low' ||
        strategy.degradation_severity === 'medium'
      rows.push({
        key: STRATEGY_KEYS.DEGRADATION_SEVERITY,
        question: `Degradation Low/Medium?\n${isLowMedium ? 'Yes' : 'No'}`,
        justification:
          strategy.degradation_severity_justification ??
          strategy.justification ??
          '—',
      })
    }

    return rows
  }, [strategy])

  const columns = useMemo<ColumnsType<StrategyRow>>(
    () =>
      strategyColumns.map((col) => {
        if (!('dataIndex' in col)) return col
        return {
          ...col,
          render: (value: string, record: StrategyRow) => (
            <CommentIndicator
              anchor={{
                type: 'zone-property',
                stepId,
                zoneId,
                property: `${ZONE_SYNTHETIC_PREFIX.STRATEGY}.${record.key}.${col.dataIndex as string}`,
              }}>
              {value}
            </CommentIndicator>
          ),
        }
      }),
    [stepId, zoneId]
  )

  if (!strategy || data.length === 0) return null

  return (
    <CommentIndicator
      anchor={{
        type: 'zone-property',
        stepId,
        zoneId,
        property: ZONE_SECTION_KEYS.NII_STRATEGY,
      }}>
      <SectionBlock>
        <SectionTitle>NII Strategy Selection</SectionTitle>
        <Table
          title={() => (
            <TableHeader>Strategy Selection (Zone {zoneIndex + 1})</TableHeader>
          )}
          columns={columns}
          dataSource={data}
          pagination={false}
          size='small'
          bordered
        />
        {effectiveType && (
          <Alert
            type='info'
            showIcon={false}
            message={
              <>
                Overall Strategy Decision:{' '}
                {STRATEGY_LABELS[effectiveType] ?? effectiveType}
                {strategy.strategy_type_override && ' (Overridden)'}
              </>
            }
            style={{ marginTop: 8, textAlign: 'center', fontWeight: 700 }}
          />
        )}
      </SectionBlock>
    </CommentIndicator>
  )
}

// ── RBI Additional Information ─────────────────────────────────────

interface RbaRow {
  key: string
  variable: string
  value: string
  justification: string
}

const rbaColumns: ColumnsType<RbaRow> = [
  { title: 'Variable', dataIndex: 'variable' },
  { title: 'Value', dataIndex: 'value', width: 100, align: 'center' },
  { title: 'Justification', dataIndex: 'justification' },
]

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const RbaInformationTable: FC<{
  rbaInfo: RbaInformation | undefined
  niiStrategy: NiiStrategy | undefined
  zoneIndex: number
  stepId: string
  zoneId: string
}> = ({ rbaInfo, niiStrategy, zoneIndex, stepId, zoneId }) => {
  const strategyType = getEffectiveStrategyType(niiStrategy)

  const data = useMemo<RbaRow[]>(() => {
    if (!strategyType || strategyType === InspectionStrategy.C) return []

    const rows: RbaRow[] = []

    const density = rbaInfo?.density_of_degradation ?? 'medium'
    rows.push({
      key: RBA_KEYS.DENSITY.key,
      variable: 'Density of Degradation (Coverage Modifier)',
      value: capitalize(density),
      justification: rbaInfo?.density_justification ?? '—',
    })

    if (strategyType === InspectionStrategy.B) {
      const accuracy = rbaInfo?.measurement_accuracy ?? 'medium'
      rows.push({
        key: RBA_KEYS.ACCURACY.key,
        variable: 'Measurement Strategy (Measurement Accuracy)',
        value: capitalize(accuracy),
        justification: rbaInfo?.measurement_accuracy_justification ?? '—',
      })

      const tolerance = rbaInfo?.tolerance_of_degradation ?? 'medium'
      rows.push({
        key: RBA_KEYS.TOLERANCE.key,
        variable: 'Tolerance of Degradation',
        value: capitalize(tolerance),
        justification: rbaInfo?.tolerance_justification ?? '—',
      })

      const homogeneity = rbaInfo?.spatial_homogeneity ?? 'medium'
      rows.push({
        key: RBA_KEYS.HOMOGENEITY.key,
        variable: 'Homogeneity of Degradation (Spatial Homogeneity)',
        value: capitalize(homogeneity),
        justification: rbaInfo?.spatial_homogeneity_justification ?? '—',
      })
    }

    return rows
  }, [rbaInfo, strategyType])

  const columns = useMemo<ColumnsType<RbaRow>>(
    () =>
      rbaColumns.map((col) => {
        if (!('dataIndex' in col)) return col
        return {
          ...col,
          render: (value: string, record: RbaRow) => (
            <CommentIndicator
              anchor={{
                type: 'zone-property',
                stepId,
                zoneId,
                property: `${ZONE_SYNTHETIC_PREFIX.RBA}.${record.key}.${col.dataIndex as string}`,
              }}>
              {value}
            </CommentIndicator>
          ),
        }
      }),
    [stepId, zoneId]
  )

  if (data.length === 0) return null

  return (
    <CommentIndicator
      anchor={{
        type: 'zone-property',
        stepId,
        zoneId,
        property: ZONE_SECTION_KEYS.RBA_INFO,
      }}>
      <SectionBlock>
        <SectionTitle>RBI Additional Information</SectionTitle>
        <Table
          title={() => (
            <TableHeader>
              RBI Additional Information (Zone {zoneIndex + 1})
            </TableHeader>
          )}
          columns={columns}
          dataSource={data}
          pagination={false}
          size='small'
          bordered
        />
      </SectionBlock>
    </CommentIndicator>
  )
}

// ── Coverage Requirements ──────────────────────────────────────────

interface CoverageRow {
  key: string
  coverageModifier: string
  consequenceOfFailure: string
  zoneArea: string
  baseCoverage: string
  measurementAccuracy: string
  spatialHomogeneity: string
  toleranceOfDegradation: string
  coverageRequired: string
  coverageArea: string
}

const coverageColumns: ColumnsType<CoverageRow> = COVERAGE_COLUMNS.map((c) => ({
  title: c.title,
  dataIndex: c.dataIndex,
  align: 'center' as const,
}))

const CoverageRequirementsTable: FC<{
  coverage: CoverageCalculation | undefined
  stepId: string
  zoneId: string
}> = ({ coverage, stepId, zoneId }) => {
  const data = useMemo<CoverageRow[]>(() => {
    if (!coverage) return []
    return [
      {
        key: 'coverage',
        coverageModifier: coverage.f_cov?.toFixed(1) ?? '—',
        consequenceOfFailure: coverage.f_cons?.toFixed(1) ?? '—',
        zoneArea: coverage.f_zone?.toFixed(2) ?? '—',
        baseCoverage: String(coverage.base_coverage_percent ?? '—'),
        measurementAccuracy: coverage.f_acc?.toFixed(1) ?? '—',
        spatialHomogeneity: coverage.f_sh?.toFixed(2) ?? '—',
        toleranceOfDegradation: coverage.f_lc?.toFixed(1) ?? '—',
        coverageRequired:
          coverage.required_coverage_percent !== undefined
            ? `${coverage.required_coverage_percent.toFixed(2)} %`
            : '—',
        coverageArea:
          coverage.required_coverage_area_sq_m !== undefined
            ? `${coverage.required_coverage_area_sq_m.toFixed(2)} m²`
            : '—',
      },
    ]
  }, [coverage])

  const columns = useMemo<ColumnsType<CoverageRow>>(
    () =>
      coverageColumns.map((col) => {
        if (!('dataIndex' in col)) return col
        return {
          ...col,
          render: (value: string) => (
            <CommentIndicator
              anchor={{
                type: 'zone-property',
                stepId,
                zoneId,
                property: `${ZONE_SYNTHETIC_PREFIX.COVERAGE}.${col.dataIndex as string}`,
              }}>
              {value}
            </CommentIndicator>
          ),
        }
      }),
    [stepId, zoneId]
  )

  if (!coverage) return null

  return (
    <CommentIndicator
      anchor={{
        type: 'zone-property',
        stepId,
        zoneId,
        property: ZONE_SECTION_KEYS.COVERAGE_REQUIREMENTS,
      }}>
      <SectionBlock>
        <SectionTitle>Coverage Requirements</SectionTitle>
        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          size='small'
          bordered
          scroll={{ x: 'max-content' }}
        />
      </SectionBlock>
    </CommentIndicator>
  )
}

// ── Per-Zone Detail Content ────────────────────────────────────────

const ZoneDetailContent: FC<{
  zone: ZoneParams
  zoneIndex: number
  cofModifier: number
  confidence: 'low' | 'med' | 'high'
  stepId: string
}> = ({ zone, zoneIndex, cofModifier, confidence, stepId }) => {
  const coverage = useMemo(
    () => calculateRequiredCoverage(zone, cofModifier, confidence),
    [zone, cofModifier, confidence]
  )

  const strategyType = getEffectiveStrategyType(zone.nii_strategy)
  const zoneId = zone.id

  return (
    <DetailContainer>
      <DamageMechanismsTable
        damageMechanisms={zone.damage_mechanisms ?? []}
        zoneIndex={zoneIndex}
        stepId={stepId}
        zoneId={zoneId}
      />

      <ZoneDetailsTable
        zone={zone}
        zoneIndex={zoneIndex}
        stepId={stepId}
        zoneId={zoneId}
      />

      <NiiStrategyTable
        strategy={zone.nii_strategy}
        zoneIndex={zoneIndex}
        stepId={stepId}
        zoneId={zoneId}
      />

      <RbaInformationTable
        rbaInfo={zone.rba_info}
        niiStrategy={zone.nii_strategy}
        zoneIndex={zoneIndex}
        stepId={stepId}
        zoneId={zoneId}
      />

      <CoverageRequirementsTable
        coverage={coverage}
        stepId={stepId}
        zoneId={zoneId}
      />

      {coverage && strategyType && (
        <CommentIndicator
          anchor={{
            type: 'zone-property',
            stepId,
            zoneId,
            property: ZONE_SECTION_KEYS.COVERAGE_BREAKDOWN,
          }}>
          <CoverageCalculationBreakdown
            zone={zone}
            coverage={coverage}
            strategyType={strategyType}
            confidence={confidence}
          />
        </CommentIndicator>
      )}
    </DetailContainer>
  )
}

// ── Consequence of Failure Summary ─────────────────────────────────

const ReadonlyCofSummary: FC<{ cof: ConsequenceOfFailure }> = ({ cof }) => (
  <StyledCard title='Consequence of Failure'>
    <CofRow>
      <CofLabel>Level:</CofLabel>
      <Tag color={COF_COLORS[cof.level] ?? 'default'}>
        {cof.level.charAt(0).toUpperCase() + cof.level.slice(1)}
      </Tag>
      <CofLabel style={{ marginLeft: 16 }}>Modifier:</CofLabel>
      <span>{getCofModifier(cof.level)}×</span>
    </CofRow>
    {cof.justification && (
      <CofJustification>{cof.justification}</CofJustification>
    )}
  </StyledCard>
)

// ── Zone Reference Images ──────────────────────────────────────────

const ZoneReferenceImages: FC<{ zones: ZoneParams[] }> = ({ zones }) => {
  const imageIds = useMemo(() => {
    const ids = zones
      .map((z) => z.zone_reference_image_id)
      .filter((id): id is string => !!id)
    return [...new Set(ids)]
  }, [zones])

  const queryBody = useMemo(
    () =>
      imageIds.length > 0
        ? { ids: imageIds, type: [RolodexEntryType.REPORT] }
        : undefined,
    [imageIds]
  )
  const { reports, isLoading: isLoadingEntries } = useRolodexEntries(
    queryBody,
    {
      swrOptions: { revalidateOnFocus: false, compare: isEqual },
    }
  )

  const imageEntries = useMemo(
    () =>
      reports
        .map((doc) => {
          const filename = doc.raw.data.filename as string | undefined
          if (!filename) return null
          return {
            id: doc.id,
            name: doc.data.visibleName || filename || doc.id.substring(0, 8),
            filename,
          }
        })
        .filter(Boolean) as Array<{
        id: string
        name: string
        filename: string
      }>,
    [reports]
  )

  const signedUrlQuery = useMemo<RolodexFilePath[] | undefined>(
    () =>
      imageEntries.length > 0
        ? imageEntries.map((e) => ({ entry_id: e.id, path: e.filename }))
        : undefined,
    [imageEntries]
  )
  const { results: signedResults, isLoading: isLoadingUrls } =
    useRolodexSignedURLs(signedUrlQuery)

  const images = useMemo(() => {
    const urlMap = new Map(signedResults.map((r) => [r.entry_id, r.signed_url]))
    return imageEntries
      .map((entry) => ({
        ...entry,
        url: urlMap.get(entry.id),
      }))
      .filter((img) => img.url) as Array<{
      id: string
      name: string
      filename: string
      url: string
    }>
  }, [imageEntries, signedResults])

  if (isLoadingEntries || isLoadingUrls || images.length === 0) return null

  return (
    <>
      {images.map((img) => (
        <StyledCard key={img.id} title={img.name}>
          <SmartImageViewer url={img.url} documentId={img.id} height={500} />
        </StyledCard>
      ))}
    </>
  )
}

// ── Inner panel (rendered inside ZoneAnalysisDataProvider) ─────────

const ZoneAnalysisReviewContent: FC = () => {
  const { zones, cof, confidence, zoneBreakdownJustification } =
    useZoneAnalysisData()
  const stepId = useActiveStepId()

  const collapseItems = useMemo(
    () =>
      zones.map((zone, idx) => ({
        key: zone.id,
        label: <ZoneRow zone={zone} readonly />,
        children: (
          <ZoneDetailContent
            zone={zone}
            zoneIndex={idx}
            cofModifier={getCofModifier(cof.level)}
            confidence={confidence}
            stepId={stepId}
          />
        ),
      })),
    [zones, cof, confidence, stepId]
  )

  return (
    <Container>
      <ZoneReferenceImages zones={zones} />
      <CommentIndicator anchor={{ type: 'cof', stepId }}>
        <ReadonlyCofSummary cof={cof} />
      </CommentIndicator>

      {zoneBreakdownJustification && (
        <CommentIndicator
          anchor={{
            type: 'field',
            stepId,
            fieldId: ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION,
          }}>
          <StyledCard title='Zone Breakdown Justification'>
            <JustificationText>{zoneBreakdownJustification}</JustificationText>
          </StyledCard>
        </CommentIndicator>
      )}

      {zones.length > 0 ? (
        <CommentIndicator
          anchor={{
            type: 'field',
            stepId,
            fieldId: ZONE_FIELD_IDS.ZONES,
          }}>
          <StyledCard title='Zone Overview Results'>
            <ZoneOverviewTable
              zones={zones}
              cofModifier={getCofModifier(cof.level)}
              confidence={confidence}
              stepId={stepId}
            />
          </StyledCard>
        </CommentIndicator>
      ) : (
        <NoDataText>No zones defined.</NoDataText>
      )}

      {zones.length > 0 && (
        <ZoneCollapse items={collapseItems} bordered={false} />
      )}
    </Container>
  )
}

// ── Main Component ─────────────────────────────────────────────────

export const ZoneAnalysisReviewPanel: FC<{
  config: Record<string, unknown>
}> = ({ config }) => {
  const zoneConfig = useMemo(
    () => zoneAnalysisConfigSchema.parse(config),
    [config]
  )

  return (
    <ZoneAnalysisDataProvider config={zoneConfig}>
      <ZoneAnalysisReviewContent />
    </ZoneAnalysisDataProvider>
  )
}

// ── Styles ─────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ZoneCollapse = styled(Collapse)`
  .ant-collapse-header {
    padding: 0 !important;
    align-items: center !important;
  }

  .ant-collapse-content-box {
    padding: 16px !important;
  }

  .ant-collapse-item {
    border-radius: 8px !important;
    margin-bottom: 8px;
    overflow: visible;
  }
`

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  .ant-table-title {
    background: rgba(255, 255, 255, 0.14);
    border-bottom: 2px solid ${({ theme }) => theme.forecast.border};
    padding: 10px 16px !important;
  }

  .ant-table-thead > tr > th {
    background: rgba(255, 255, 255, 0.08) !important;
  }

  .ant-table-tbody > tr > td {
    background: transparent !important;
  }
`

const SectionTitle = styled.div`
  font-weight: 700;
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.defaultText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`

const TableHeader = styled.div`
  font-weight: 700;
  font-size: 13px;
  text-align: center;
`

const CofRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CofLabel = styled.span`
  font-weight: 600;
`

const CofJustification = styled.div`
  margin-top: 8px;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const JustificationText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
`

const NoDataText = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-style: italic;
  padding: 0.5rem 0;
`
